import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { v4 } from 'uuid';

import { DAILY } from '../../Constants/Signal';
import {
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  CAN_SCAN,
  COMMAND,
  MAINTENANCE,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
  TEXT_TAGS,
} from '../../Constants/WidgetTypes';
import CreateWidgetHelper from '../../Lib/CreateWidgetHelper';
import { handleImageFileConversion } from '../../Lib/FileHelpers';
import TagService from '../../Services/TagService';
import TemplateDashboardService from '../../Services/TemplateDashboardService';
import WidgetUtils from '../../Lib/WidgetUtils';

import CreateWidgetModalDisplay from './CreateWidgetModalDisplay';

export default memo(CreateWidgetModal);

const initState = {
  activeImageCoordinate: new Map(),
  createRequestPending: false,
  datapointsMap: [],
  displayTextAsGrid: false,
  error: '',
  helpText: '',
  image: '',
  imageCoordinatesMap: new Map(),
  selectedAggregation: DAILY,
  selectedType: {},
  showTimeSinceLastUpdated: false,
  tagOptions: [],
  tagsLoaded: false,
  widgetName: '',
  widgetSignals: [],
  widgetType: null,
  yMax: undefined,
  yMin: undefined,
};

function CreateWidgetModal(props) {
  const [state, updateState] = useState(() => {
    const { gtemplate, gtemplate: { datapoints } = {} } = props.asset || {};
    let configError = '';
    if (!Object.keys(gtemplate || {}).length) {
      configError =
        'Asset must be assigned to a model before a smart tile can be created. This can be done by clicking "Edit" in the Settings dropdown menu.';
    } else if (!Array.isArray(datapoints) || !datapoints?.length) {
      configError =
        'Asset model does not currently have any collections or signals configured.';
    }

    return { ...initState, configError };
  });

  const tagService = useMemo(() => new TagService(), []);
  const templateDashboardService = useMemo(
    () => new TemplateDashboardService(),
    []
  );
  const widgetUtils = useMemo(() => new WidgetUtils(), []);
  const source = CancelToken.source();

  const maintenanceSelectRef = useRef();

  const widgetOptions = useMemo(
    () =>
      widgetUtils.types.filter(({ value }) => {
        switch (value) {
          case COMMAND:
            return props.currentOrg?.features?.remote;
          case BAR_ENGINE_UTILIZATION:
          case BAR_FUEL_UTILIZATION:
          case TEXT_ENGINE:
          case TEXT_FUEL:
            return props.currentOrg?.features?.utilization_reporting?.enabled;
          default:
            return true;
        }
      }),
    [
      props.currentOrg?.features?.remote,
      props.currentOrg?.features?.utilization_reporting?.enabled,
      widgetUtils.types,
    ]
  );

  const clearImage = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      activeImageCoordinate: new Map(),
      image: '',
      imageCoordinatesMap: new Map(),
    }));
  }, []);

  const createWidget = async () => {
    const {
      displayTextAsGrid,
      image,
      imageCoordinatesMap,
      selectedAggregation,
      showTimeSinceLastUpdated,
      widgetName,
      widgetSignals,
      widgetType,
      yMin,
      yMax,
    } = state;
    const {
        asset: { gtemplateID, gtemplate: { datapoints = [] } = {} },
        dashboardID,
        toggleModal,
        updateWidgets,
      } = props,
      maintenanceSelectValue = maintenanceSelectRef?.current?.getValue?.();

    if (!widgetType) {
      return updateState((prevState) => ({
        ...prevState,
        error: 'Smart Tile Type must be selected.',
      }));
    }

    if (widgetType === MAINTENANCE && !maintenanceSelectValue?.length) {
      return updateState((prevState) => ({
        ...prevState,
        error: 'Please select a Maintenance Reminder to display.',
      }));
    }

    try {
      updateState((prevState) => ({
        ...prevState,
        createRequestPending: true,
        error: '',
      }));
      const newWidget = new CreateWidgetHelper(widgetType).addSettingsByType({
        displayTextAsGrid,
        image,
        imageCoordinatesMap,
        maintenanceSelectValue,
        selectedAggregation,
        selectedSignals: widgetSignals,
        showTimeSinceLastUpdated,
        signalCollections: datapoints,
        title: widgetName,
        yMin: Number.parseFloat(yMin),
        yMax: Number.parseFloat(yMax),
      });

      const { widgets } =
        await templateDashboardService.createTemplateDashboardWidget(
          gtemplateID,
          dashboardID,
          newWidget,
          source.token
        );
      if (Array.isArray(widgets) && widgets.length) {
        return updateWidgets(widgets.reverse());
      }

      toggleModal();
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        createRequestPending: false,
        error,
      }));
    }
  };

  const handleAddRange = (outerIndex, innerIndex) => {
    if (innerIndex >= 4) return;
    try {
      const widgetSignals = state.widgetSignals.map((s, index) => {
        if (index !== outerIndex) return s;
        const validRange = s.ranges?.every?.(
          ({ color, label, maxValue, minValue } = {}) => {
            return color && label && maxValue && minValue;
          }
        );
        if (!validRange) {
          throw new Error(
            'Each range must have a Min/Max Value, Label and Color.'
          );
        }

        const { maxValue } = s.ranges[innerIndex] || {};
        return {
          ...s,
          ranges: s.ranges.concat({ id: v4(), minValue: maxValue }),
        };
      });
      updateState((prevState) => ({ ...prevState, error: '', widgetSignals }));
    } catch ({ message: error }) {
      updateState((prevState) => ({ ...prevState, error }));
    }
  };

  const handleAggregationSelect = useCallback((selected) => {
    updateState((prevState) => ({
      ...prevState,
      selectedAggregation: selected?.value,
    }));
  }, []);

  const handleAddImageOverlaySignal = useCallback((coordinates, signal) => {
    updateState((prevState) => {
      const imageCoordinatesMap = new Map(prevState.imageCoordinatesMap).set(
        coordinates,
        signal
      );
      return {
        ...prevState,
        imageCoordinatesMap,
        activeImageCoordinate: new Map(),
      };
    });
  }, []);

  const handleAddStaticTextLink = useCallback(() => {
    updateState((prevState) => {
      const [{ links: prevLinks = [] }] = prevState.widgetSignals;
      const links = [...prevLinks, { display: '', link: '' }];
      return {
        ...prevState,
        widgetSignals: [{ ...prevState.widgetSignals[0], links }],
      };
    });
  }, []);

  const handleAddWidgetCommand = useCallback((command, index) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) =>
        i === index ? command : s
      );
      return { ...prevState, widgetSignals, error: '' };
    });
  }, []);

  const handleAddWidgetSignal = useCallback(() => {
    updateState((prevState) => {
      if (
        prevState.widgetSignals.length === prevState.selectedType.maxSignals
      ) {
        return prevState;
      }

      const rowToAdd = widgetUtils.nonSignalTypes.has(prevState.widgetType)
        ? {}
        : widgetUtils.widgetSignalDefault;
      return {
        ...prevState,
        widgetSignals: prevState.widgetSignals.concat(rowToAdd),
      };
    });
  }, [widgetUtils.nonSignalTypes, widgetUtils.widgetSignalDefault]);

  const handleColorInput = useCallback(
    ({ target: { value } }, outerIndex, innerIndex) => {
      const trimmedInput = value.trim();
      const color = trimmedInput.startsWith('#')
        ? trimmedInput
        : '#'.concat(trimmedInput);
      updateState((prevState) => {
        const widgetSignals = prevState.widgetSignals.map((s, i) => {
          if (i !== outerIndex) return s;
          const ranges = s.ranges.map((r, index) =>
            index === innerIndex ? { ...r, color } : r
          );
          return { ...s, ranges };
        });
        return { ...prevState, widgetSignals };
      });
    },
    []
  );

  const handleColorSelected = useCallback((color, outerIndex, innerIndex) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) => {
        if (i !== outerIndex) return s;
        const ranges = s.ranges.map((r, index) =>
          index === innerIndex ? { ...r, color } : r
        );
        return { ...s, ranges };
      });
      return { ...prevState, widgetSignals };
    });
  }, []);

  const handleCoordinateClick = useCallback((x, y, overlayConfig) => {
    updateState((prevState) => {
      const activeImageCoordinate = new Map([
        ['x', Number(x)],
        ['y', Number(y)],
        ['overlayConfig', overlayConfig],
      ]);
      return { ...prevState, activeImageCoordinate };
    });
  }, []);

  const handleDatapointSelect = useCallback((datapoint, index) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) => {
        if (i !== index) return s;
        return { ...s, datapoint, signal: {} };
      });
      return { ...prevState, widgetSignals };
    });
  }, []);

  const handleDisplayTextAsGridClick = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      displayTextAsGrid: !prevState.displayTextAsGrid,
    }));
  }, []);

  const handleImageClick = useCallback(({ clientX, clientY, target }) => {
    const { bottom, left, right, top } = target.getBoundingClientRect?.() || {};

    const xCalc = ((clientX - left) * 100) / (right - left);
    const x = Number(xCalc.toFixed());

    const yCalc = ((clientY - top) * 100) / (bottom - top);
    const y = Number(yCalc.toFixed());

    const activeImageCoordinate = new Map([
      ['x', clientX],
      ['y', clientY],
      ['overlayConfig', { x, y }],
    ]);
    updateState((prevState) => ({ ...prevState, activeImageCoordinate }));
  }, []);

  const handleImageSelect = ([file]) => {
    const handleError = () => {
      updateState({
        ...state,
        error: 'Error loading image. Please try again.',
      });
    };
    const handleSuccess = (image) => updateState({ ...state, image });
    handleImageFileConversion(file, handleError, handleSuccess, 500);
  };

  const handleMinMaxValueUpdate = useCallback(
    ({ target: { name, value } }, index) => {
      updateState((prevState) => {
        const widgetSignals = prevState.widgetSignals.map((s, i) =>
          i === index ? { ...s, [name]: value } : s
        );
        return { ...prevState, widgetSignals };
      });
    },
    []
  );

  const handleNameAndYScaleUpdate = useCallback(
    ({ target: { name, value } }) => {
      updateState((prevState) => ({ ...prevState, [name]: value }));
    },
    []
  );

  const handleCommandTypeSelect = useCallback((selectedType) => {
    const { helpText, value: widgetType } = selectedType;
    updateState((prevState) => ({
      ...prevState,
      helpText,
      selectedType,
      widgetSignals: [{}],
      widgetType,
    }));
  }, []);

  const handleOptionDisabled = useCallback(
    (signal) => {
      return state.widgetSignals.some(
        (widgetSignal) => signal?.value === widgetSignal?.signal?.value
      );
    },
    [state.widgetSignals]
  );

  const handleRangeValueUpdate = (
    { target: { name, value } },
    outerIndex,
    innerIndex
  ) => {
    if (innerIndex && name === 'minValue') return;

    const widgetSignals = state.widgetSignals.map((s, index) => {
      if (index !== outerIndex) return s;
      const ranges = s.ranges.map((r, i) => {
        switch (true) {
          case i === innerIndex:
            return { ...r, [name]: value };
          case name === 'maxValue' && innerIndex === i - 1:
            return { ...r, minValue: value };
          default:
            return r;
        }
      });
      return { ...s, ranges };
    });
    updateState((prevState) => ({ ...prevState, error: '', widgetSignals }));
  };

  const handleRemoveImageOverlaySignal = useCallback((coordinates) => {
    updateState((prevState) => {
      const imageCoordinatesMap = new Map(prevState.imageCoordinatesMap);
      imageCoordinatesMap.delete(coordinates);
      return {
        ...prevState,
        imageCoordinatesMap,
        activeImageCoordinate: new Map(),
      };
    });
  }, []);

  const handleRemoveRange = useCallback((outerIndex, innerIndex) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, index) => {
        if (index !== outerIndex) return s;
        const filtered = s.ranges
          .map((r, i, arr) => {
            if (!innerIndex || i !== innerIndex + 1) return r;
            const { maxValue } = arr[innerIndex - 1] || {};
            return { ...r, minValue: maxValue };
          })
          .filter((r, i) => i !== innerIndex);
        const ranges = filtered.length ? filtered : [{ id: v4() }];
        return { ...s, ranges };
      });
      return { ...prevState, error: '', widgetSignals };
    });
  }, []);

  const handleRemoveStaticTextLink = useCallback((index) => {
    updateState((prevState) => {
      const links = prevState.widgetSignals[0].links.filter(
        (_, i) => i !== index
      );
      return {
        ...prevState,
        widgetSignals: [{ ...prevState.widgetSignals[0], links }],
      };
    });
  }, []);

  const handleRemoveWidgetSignal = useCallback(
    (index) => {
      updateState((prevState) => {
        const update = prevState.widgetSignals.filter((x, i) => i !== index);
        const widgetSignals = update.length
          ? update
          : [
              widgetUtils.nonSignalTypes.has(prevState.widgetType)
                ? {}
                : widgetUtils.widgetSignalDefault,
            ];
        return { ...prevState, widgetSignals };
      });
    },
    [widgetUtils.nonSignalTypes, widgetUtils.widgetSignalDefault]
  );

  const handleSignalSelect = useCallback((signal, index) => {
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) =>
        i === index ? { ...s, signal } : s
      );
      return { ...prevState, widgetSignals };
    });
  }, []);

  const handleSignalTypeSelect = useCallback(
    (selectedType) => {
      const { helpText, value: widgetType } = selectedType;

      const datapointsMap = widgetUtils.createDatapointMapForDisplay(
        props.asset?.gtemplate?.datapoints || [],
        widgetType === CAN_SCAN
      );
      updateState((prevState) => ({
        ...prevState,
        datapointsMap,
        helpText,
        selectedType,
        widgetSignals: [widgetUtils.widgetSignalDefault],
        widgetType,
      }));
    },
    [props.asset?.gtemplate?.datapoints, widgetUtils]
  );

  const handleStaticTextInput = useCallback(({ target: { value } }) => {
    updateState((prevState) => ({
      ...prevState,
      widgetSignals: [{ ...prevState.widgetSignals[0], value }],
      error: '',
    }));
  }, []);

  const handleStaticTextLinkInput = useCallback(
    ({ target: { name, value } }, i) => {
      updateState((prevState) => {
        const links = prevState.widgetSignals[0].links?.map((link, index) =>
          index === i ? { ...link, [name]: value } : link
        );
        return {
          ...prevState,
          widgetSignals: [{ ...prevState.widgetSignals[0], links }],
        };
      });
    },
    []
  );

  const handleTagSelect = useCallback((selectedTag, index) => {
    if (!selectedTag) return;
    updateState((prevState) => {
      const widgetSignals = prevState.widgetSignals.map((s, i) =>
        i === index ? selectedTag : s
      );
      return { ...prevState, widgetSignals };
    });
  }, []);

  const handleTextStaticTypeSelect = useCallback((selectedType) => {
    const { helpText, value: widgetType } = selectedType;
    updateState((prevState) => ({
      ...prevState,
      helpText,
      selectedType,
      widgetSignals: [{ value: '' }],
      widgetType,
    }));
  }, []);

  const handleTextTagTypeSelect = async (selectedType) => {
    try {
      const { helpText, value: widgetType } = selectedType;
      updateState((prevState) => ({
        ...prevState,
        helpText,
        selectedType,
        tagsLoading: true,
        widgetSignals: [{}],
        widgetType,
      }));
      const params = { orgID: props.asset?.orgID };
      const tags = await tagService.getTags(params, source.token);
      const tagSet = new Set(tags.map(({ name }) => name));
      const tagOptions = Array.from(tagSet).map((t) => ({
        label: t,
        value: t,
      }));
      updateState((prevState) => ({
        ...prevState,
        tagsLoaded: true,
        tagsLoading: false,
        tagOptions,
      }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        tagsLoading: false,
      }));
    }
  };

  const handleTimeSinceLastUpdatedClick = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      showTimeSinceLastUpdated: !prevState.showTimeSinceLastUpdated,
    }));
  }, []);

  const handleWidgetTypeSelect = async (selectedType) => {
    switch (selectedType?.value) {
      case TEXT_STATIC:
        handleTextStaticTypeSelect(selectedType);
        break;
      case TEXT_TAGS:
        handleTextTagTypeSelect(selectedType);
        break;
      case COMMAND:
        handleCommandTypeSelect(selectedType);
        break;
      default:
        handleSignalTypeSelect(selectedType);
        break;
    }
  };

  const toggleImageOverlayModal = useCallback(() => {
    updateState((prevState) => ({
      ...prevState,
      activeImageCoordinate: new Map(),
    }));
  }, []);

  useEffect(() => source.cancel, []);

  return (
    <CreateWidgetModalDisplay
      {...props}
      {...state}
      clearImage={clearImage}
      createWidget={createWidget}
      handleAddRange={handleAddRange}
      handleAddImageOverlaySignal={handleAddImageOverlaySignal}
      handleAddStaticTextLink={handleAddStaticTextLink}
      handleAddWidgetCommand={handleAddWidgetCommand}
      handleAddWidgetSignal={handleAddWidgetSignal}
      handleAggregationSelect={handleAggregationSelect}
      handleColorInput={handleColorInput}
      handleColorSelected={handleColorSelected}
      handleCoordinateClick={handleCoordinateClick}
      handleDatapointSelect={handleDatapointSelect}
      handleDisplayTextAsGridClick={handleDisplayTextAsGridClick}
      handleImageClick={handleImageClick}
      handleImageSelect={handleImageSelect}
      handleMinMaxValueUpdate={handleMinMaxValueUpdate}
      handleNameAndYScaleUpdate={handleNameAndYScaleUpdate}
      handleOptionDisabled={handleOptionDisabled}
      handleRangeValueUpdate={handleRangeValueUpdate}
      handleRemoveImageOverlaySignal={handleRemoveImageOverlaySignal}
      handleRemoveRange={handleRemoveRange}
      handleRemoveStaticTextLink={handleRemoveStaticTextLink}
      handleRemoveWidgetSignal={handleRemoveWidgetSignal}
      handleSignalSelect={handleSignalSelect}
      handleStaticTextInput={handleStaticTextInput}
      handleStaticTextLinkInput={handleStaticTextLinkInput}
      handleTagSelect={handleTagSelect}
      handleTimeSinceLastUpdatedClick={handleTimeSinceLastUpdatedClick}
      handleWidgetTypeSelect={handleWidgetTypeSelect}
      maintenanceSelectRef={maintenanceSelectRef}
      timeAggregationOptions={widgetUtils.timeAggregationOptions}
      toggleImageOverlayModal={toggleImageOverlayModal}
      updateState={updateState}
      widgetOptions={widgetOptions}
    />
  );
}

CreateWidgetModal.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    gtemplate: PropTypes.shape({
      commands: PropTypes.arrayOf(PropTypes.object),
      datapoints: PropTypes.arrayOf(PropTypes.object),
    }),
    gtemplateID: PropTypes.string,
    orgID: PropTypes.string,
  }),
  currentOrg: PropTypes.shape({
    features: PropTypes.shape({
      remote: PropTypes.bool,
      utilization_reporting: PropTypes.shape({ enabled: PropTypes.bool }),
    }),
  }),
  dashboardID: PropTypes.string,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
  updateWidgets: PropTypes.func,
};
